import React from 'react';

import Data, { _F } from 'data';

import './Rapport.scss';
import { getIconPath } from 'common';
import classNames from 'classnames';
import { ItemIcon, WithItemTooltip, WithMusicTooltip, WithSocialActionTooltip } from 'tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'ui/tooltip';

const rapportTabs = [
  {key: 'rewards', tip: 'Rewards'},
  {key: 'actions', tip: 'Actions'},
  {key: 'stages', tip: 'Stages'},
];

const rapportTiers = [
  'sys.intimate.grade_normal',
  'sys.intimate.grade_interest',
  'sys.intimate.grade_intimate',
  'sys.intimate.grade_trust',
  'sys.friendship.subgrade_10',
];

function RapportReward({ data }) {
  const item = data && data.type === 1 && Data.items[data.id];
  if (!item) return <div className="lap-rapport-reward lap-empty"/>;
  return (
    <div className="lap-rapport-reward">
      <WithItemTooltip item={data.id}>
        <div className={`lap-item-background lap-item-${Data.itemGrades[item.grade || 0].color}`}>
          <ItemIcon item={item}/>
        </div>
      </WithItemTooltip>
      {data.amount > 1 && <div className="lap-count">{data.amount}</div>}
    </div>
  );
}

function RapportRewards({ data }) {
  return rapportTiers.slice(0, data.bestFriend ? 5 : 4).map((tip, tier) => (
    <div className="lap-rapport-tier" key={tier}>
      <div className={`lap-rapport-label lap-tier-${tier + 1}`}>{_F(tip)}</div>
      {[0, 1, 2, 3].map(index => <RapportReward key={index} data={data.rewards[tier]?.[index]}/>)}
    </div>
  ));
}

function RapportConditionTooltip({ data }) {
  return (
    <div>
      <div>Requires:</div>
      {Object.entries(Data.socialStats).filter(([stat]) => data[stat]).map(([stat, {tip}]) => (
        <div key={stat}><span className={`lap-SocialIcon lap-social-${stat}`}/> {data[stat]} {_F(tip)}</div>
      ))}
    </div>
  );
}

function RapportActionCondition({ data }) {
  if (!Object.keys(Data.socialStats).some(stat => data[stat])) return null;
  return (
    <Tooltip tooltip={<RapportConditionTooltip data={data}/>}>
      <FontAwesomeIcon icon={faExclamationCircle} className="lap-rapport-condition"/>
    </Tooltip>
  );
}

const RapportActionNode = ({ className, name, data, children, ...props }) => (
  <div className={classNames("lap-rapport-action", className)} {...props}>
    {children}
    <div className="lap-rapport-action-info">
      <div className="lap-action-name">
        <RapportActionCondition data={data}/>
        {name}
      </div>
      <div className="lap-action-points">{_F('sys.friendship.point_info', data.points)}</div>
    </div>
  </div>
);

function RapportActionItem({ data }) {
  const item = Data.items[data.id];
  if (!item) return null;
  return (
    <WithItemTooltip item={data.id}>
      <RapportActionNode className={`lap-rapport-item lap-item-${Data.itemGrades[item.grade || 0].color}`} name={item.name} data={data}>
        <ItemIcon item={item}/>
      </RapportActionNode>
    </WithItemTooltip>
  );
}

function RapportActionSocial({ data }) {
  const social = Data.socialAction[data.id];
  if (!social) return null;
  return (
    <WithSocialActionTooltip id={data.id}>
      <RapportActionNode name={social.name} data={data}>
        <div className="lap-skill-icon" style={social.icon ? {backgroundImage: `url(${getIconPath(social.icon)}`} : null}/>
      </RapportActionNode>
    </WithSocialActionTooltip>
  );
}


function RapportActionMusic({ data }) {
  const music = Data.music[data.id];
  if (!music) return null;
  return (
    <WithMusicTooltip id={data.id}>
      <RapportActionNode name={music.name} data={data}>
        <div className="lap-skill-icon" style={music.icon ? {backgroundImage: `url(${getIconPath(music.icon)}`} : null}/>
      </RapportActionNode>
    </WithMusicTooltip>
  );
}

function RapportAction({ data }) {
  if (data.type === 1) return <RapportActionItem data={data}/>;
  if (data.type === 2) return <RapportActionSocial data={data}/>;
  if (data.type === 3) return <RapportActionMusic data={data}/>;
  return null;
}

function RapportActions({ data }) {
  const {items, social, music} = React.useMemo(() => ({
    items: data.actions.filter(act => act.type === 1).sort((a, b) => {
      const ia = Data.items[a.id]?.grade || 0, ib = Data.items[b.id]?.grade || 0;
      if (ia !== ib) return ia - ib;
      return b.points - a.points;
    }),
    social: data.actions.filter(act => act.type === 2).sort((a, b) => a.points - b.points),
    music: data.actions.filter(act => act.type === 3).sort((a, b) => a.points - b.points),
  }), [data]);
  return (
    <>
      {!!music.length && <>
        <div className="lap-rapport-action-header">{_F('sys.music.fla_sheet_music')}</div>
        <div className="lap-rapport-actions">
          {music.map(act => <RapportAction key={act.id} data={act}/>)}
        </div>
      </>}
      {!!social.length && <>
        <div className="lap-rapport-action-header">{_F('sys.social.window_title')}</div>
        <div className="lap-rapport-actions">
          {social.map(act => <RapportAction key={act.id} data={act}/>)}
        </div>
      </>}
      {!!items.length && <>
        <div className="lap-rapport-action-header">{_F('sys.intimate.window_demanditem')}</div>
        <div className="lap-rapport-actions">
          {items.map(act => <RapportAction key={act.id} data={act}/>)}
        </div>
      </>}
    </>
  );
}

const stages = [
  'sys.friendship.subgrade_0',
  'sys.friendship.subgrade_1',
  'sys.friendship.subgrade_2',
  'sys.friendship.subgrade_3',
  'sys.friendship.subgrade_4',
  'sys.friendship.subgrade_5',
  'sys.friendship.subgrade_6',
  'sys.friendship.subgrade_7',
  'sys.friendship.subgrade_8',
  'sys.friendship.subgrade_9',
  'sys.friendship.subgrade_10',
  'sys.friendship.subgrade_11',
];

function RapportStages({ data }) {
  const points = Data.npcGrade[data.grade];
  if (!points) return null;
  let total = 0;
  return (
    <table className="lap-rapport-stages">
      <thead>
        <tr><td>Stage</td><td>Points</td><td>Total</td></tr>
      </thead>
      <tbody>
        {stages.slice(1, data.bestFriend ? 10 : 9).map((name, index) => (
          <tr key={index}>
            <td className={`lap-stage-${index + 1}`}>{_F(name)}</td>
            <td>{points[index + 1]}</td>
            <td>{total += points[index + 1]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function RapportNpc({ id }) {
  const [tab, setTab] = React.useState(rapportTabs[0].key);
  const data = Data.npc[id];
  if (!data) return null;
  return (
    <div className="lap-RapportNpc">
      <div className="lap-rapport-header">
        <div className="lap-rapport-image" style={{backgroundImage: `url(${getIconPath(data.image)})`}}/>
        <div className="lap-rapport-info">
          <div className="lap-rapport-name">{data.name}</div>
          <div className="lap-rapport-zone">{Data.zone[data.zone]?.name || 'Unknown zone'}</div>
          {!!data.area && <div className="lap-rapport-area">{data.area}</div>}
        </div>
      </div>
      <div className="lap-rapport-nav">
        {rapportTabs.map(({key, tip}) => (
          <div key={key} className={classNames("lap-button", {"lap-active": key === tab})} onClick={() => setTab(key)}>{tip}</div>
        ))}
      </div>
      <div className="lap-rapport-details">
        {tab === 'rewards' && <RapportRewards data={data}/>}
        {tab === 'actions' && <RapportActions data={data}/>}
        {tab === 'stages' && <RapportStages data={data}/>}
      </div>
    </div>
  );
}
